/* ==========================================================================
   Runway Spotlight
   Scroll-driven pinned module. GSAP + ScrollTrigger are pulled from the CDN
   the first time a page actually contains this section, so nothing is added
   to the global theme bundle.

   Timeline note: every duration/position below is expressed in *scroll pixels*
   (the timeline is scrubbed against a pin of the same length), which is why the
   numbers look like 1100 rather than 1.1.
   ========================================================================== */

(function () {
    'use strict';

    var GSAP_VERSION = '3.13.0';
    var CDN = 'https://cdn.jsdelivr.net/npm/gsap@' + GSAP_VERSION + '/dist/';

    var CLIP = {
        on: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
        offTop: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
        offBottom: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)'
    };

    /* cubic-bezier(.785,.135,.15,.86) === easeInOutCirc */
    var THE_CURVE = 'circ.inOut';

    /* Scatter keyframes for the five intro thumbnails.
       Each property is an array of states; each state is [desktop, mobile].
       null = leave the property alone for that state. 'G' = park off the top.

       GSAP sets xPercent/yPercent to 0, so x/y place the element's TOP-LEFT
       corner relative to the centre of the panel. In --u units the mobile
       canvas is 375 wide, so an image spans [187.5 + x, 187.5 + x + w] and the
       mobile lane is kept inside 0..375 - images 1, 3 and 4 used to sit 72.5u,
       31.5u and 60.5u outside it and were clipped by .pin's overflow:hidden.
       Re-check that span when editing any mobile x or w. */
    var KEYFRAMES = [
        {
            w: [[253, 210]],
            h: [[317, 263]],
            x: [[-461, -180]],
            y: [[-132, -157], ['G', 'G']],
            z: 2
        },
        {
            w: [[201, 201]],
            h: [[251, 251]],
            x: [[-259, -58]],
            y: [[-285, -310], ['G', 'G']]
        },
        {
            w: [[193, 193], [427, null]],
            h: [[239, 239], [535, null]],
            x: [[-175, -16], [-660, null]],
            y: [[-195, -221], [-300, 'G'], ['G', null]]
        },
        {
            w: [[177, 131], [606, null]],
            h: [[126, 93], [431, null]],
            x: [[44, 48], [-25, null]],
            y: [[44, 206], [-300, 'G'], ['G', null]],
            z: 2
        },
        {
            w: [[115, 85]],
            h: [[154, 114]],
            x: [[257, -50]],
            y: [[185, 139], [-100, 'G'], ['G', null]]
        },
        /* 6 and 7 fill the empty upper-right and lower-left corners. They have
           no second state, so like images 1 and 2 they simply leave in one move
           — scheduled with the first wave, well before the two lower images
           start at 2300, which keeps the closing beat (and lastExitStart,
           which decides when the panel unpins) exactly as it was. */
        {
            w: [[250, 130]],
            h: [[250, 130]],
            x: [[179, -175]],
            y: [[-275, -330], ['G', 'G']]
        },
        {
            w: [[250, 130]],
            h: [[254, 130]],
            x: [[-242, -180]],
            y: [[118, 190], ['G', 'G']]
        }
    ];

    var S1_POS = [1100, 700, 1000, 1000, 1150, 950, 1050];
    var S1_DUR_DESKTOP = [1100, 1300, 850, 850, 500, 1200, 1150];
    var S1_DUR_MOBILE = [1100, 1300, 1100, 1500, 850, 1200, 1150];
    var S1_EASE_DESKTOP = [
        'power1.in',
        'power1.in',
        'power1.inOut',
        'power1.inOut',
        'power1.in',
        'power1.in',
        'power1.in'
    ];
    var S2_POS = [0, 0, 1900, 2300, 2300, 0, 0];
    var S2_DUR = [0, 0, 1250, 950, 950, 0, 0];

    /* ---------------------------------------------------------------- loader */

    var libPromise = null;

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = src;
            s.async = false;
            s.onload = resolve;
            s.onerror = function () {
                reject(new Error('Failed to load ' + src));
            };
            document.head.appendChild(s);
        });
    }

    function loadLibs() {
        if (window.gsap && window.ScrollTrigger) return Promise.resolve();
        if (!libPromise) {
            libPromise = loadScript(CDN + 'gsap.min.js')
                .then(function () {
                    return loadScript(CDN + 'ScrollTrigger.min.js');
                })
                .then(function () {
                    window.gsap.registerPlugin(window.ScrollTrigger);
                });
        }
        return libPromise;
    }

    /* ------------------------------------------------------- page metrics */

    /* The site header is fixed AND transparent. The panel therefore stops at the
       header's bottom edge (what "the top touches the header" means), and the
       124px strip above it stays clear so whatever is above — the hero video —
       keeps scrolling out through it naturally instead of being cut off. The
       panel body itself is opaque, so nothing shows through the panel. */
    function headerOffset() {
        var el =
            document.querySelector('#shopify-section-SiteHeader') ||
            document.querySelector('.site-header--wrapper');
        if (!el) return 0;
        var pos = window.getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') return 0;
        return Math.round(el.getBoundingClientRect().height) || 0;
    }

    /* ScrollTrigger measures start/end once. Anything above this section that
       gains height late — a hero video, a lazy image, an app banner — shifts the
       section down afterwards and the pin then engages at the wrong scroll
       position (it appears stuck over the content above). Re-measure whenever
       the page height actually moves. */
    var refreshTimer = null;
    var selfTriggered = false;

    function scheduleRefresh() {
        if (selfTriggered || !window.ScrollTrigger) return;
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(function () {
            selfTriggered = true;
            window.ScrollTrigger.refresh();
            setTimeout(function () {
                selfTriggered = false;
            }, 100);
        }, 250);
    }

    var watchingPage = false;

    function watchPageMetrics() {
        if (watchingPage) return;
        watchingPage = true;
        window.addEventListener('load', scheduleRefresh);
        if (window.ResizeObserver) {
            new ResizeObserver(scheduleRefresh).observe(document.body);
            var header =
                document.querySelector('#shopify-section-SiteHeader') ||
                document.querySelector('.site-header--wrapper');
            if (header) new ResizeObserver(scheduleRefresh).observe(header);
        }
    }

    /* ----------------------------------------------------------- instance */

    function Spotlight(root) {
        this.root = root;
        this.pin = root.querySelector('.pin');
        this.carousel = root.querySelector('.carousel');
        this.introText = root.querySelector('.intro .text');
        this.introMedia = toArray(root.querySelectorAll('.intro > .images > .rf-media'));
        this.introInner = this.introMedia
            .map(function (el) {
                return el.firstElementChild;
            })
            .filter(Boolean);
        this.bgImages = toArray(root.querySelectorAll('.carousel > .bg > .image'));
        this.bgFirstMedia = root.querySelector('.carousel > .bg > .image.is-0 > .rf-media');
        this.fgItems = toArray(root.querySelectorAll('.carousel > .fg > .rf-media'));
        this.textInners = toArray(root.querySelectorAll('.carousel > .texts-wrap > .text > .inner'));
        this.slideCount = this.bgImages.length;
        /* Slides are optional. With none, the module is just the intro: the
           thumbnails scatter, collapse and clear off the top, and the pin ends
           on the centred heading — everything the carousel would drive is
           skipped rather than half-built. */
        this.hasSlides = !!this.carousel && this.slideCount > 0;
        this.snap = root.getAttribute('data-snap') !== 'false';

        this.index = 0;
        this.label = null;
        this.width = 0;
        this.textTl = null;

        this.onResize = debounce(this.handleResize.bind(this), 200);
    }

    Spotlight.prototype.mount = function () {
        if (!this.pin) return;
        if (!this.hasSlides && !this.introMedia.length) {
            /* nothing to choreograph — fall back to a plain, unpinned block */
            this.root.classList.add('is-static');
            return;
        }
        this.build();
        watchPageMetrics();
        scheduleRefresh();
        window.addEventListener('resize', this.onResize);
        window.addEventListener('orientationchange', this.onResize);
    };

    Spotlight.prototype.destroy = function () {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onResize);
        this.teardown();
    };

    Spotlight.prototype.teardown = function () {
        var gsap = window.gsap;
        if (this.trigger) {
            this.trigger.kill(true);
            this.trigger = null;
        }
        if (this.master) {
            this.master.kill();
            this.master = null;
        }
        if (this.textTl) {
            this.textTl.kill();
            this.textTl = null;
        }
        gsap.killTweensOf(this.fgItems);
        var cleared = []
            .concat(this.introMedia, this.introInner, this.fgItems, this.textInners, [
                this.carousel,
                this.introText,
                this.bgFirstMedia
            ])
            .filter(Boolean);
        gsap.set(cleared, { clearProps: 'all' });
        this.bgImages.forEach(function (el) {
            el.classList.remove('is-visible', 'is-past');
        });
        this.root.style.height = '';
        this.index = 0;
        this.label = null;
    };

    Spotlight.prototype.handleResize = function () {
        /* Mobile browsers fire resize when the URL bar collapses. Only a real
           width change invalidates the layout maths; a height change just needs
           ScrollTrigger to re-measure. */
        if (window.innerWidth === this.width) {
            window.ScrollTrigger.refresh();
            return;
        }
        this.teardown();
        this.build();
    };

    Spotlight.prototype.build = function () {
        var self = this;
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger;

        /* publish the header offset first — the panel's height derives from it,
           and everything below measures against the laid-out panel */
        this.root.style.setProperty('--rf-header-h', headerOffset() + 'px');

        var vw = window.innerWidth;
        var vh = this.pin.offsetHeight || window.innerHeight;
        var isMobile = vw <= 960;
        var isTouch = window.matchMedia('(hover: none)').matches;
        var u = isMobile ? vw / 375 : Math.min(vw, 1440) / 1440;
        var parkY = -(vh * 1.5);
        var hasSlides = this.hasSlides;

        this.width = vw;

        function sel(desktop, mobile) {
            return isMobile ? mobile : desktop;
        }

        function stateFor(kf, stateIndex) {
            var lane = isMobile ? 1 : 0;
            var out = {};
            function read(arr) {
                if (!arr || stateIndex >= arr.length) return undefined;
                var v = arr[stateIndex][lane];
                return v === null ? undefined : v;
            }
            var w = read(kf.w);
            var h = read(kf.h);
            var x = read(kf.x);
            var y = read(kf.y);
            if (w !== undefined) out.width = w * u;
            if (h !== undefined) out.height = h * u;
            if (x !== undefined) out.x = x * u;
            if (y !== undefined) out.y = y === 'G' ? parkY : y * u;
            if (kf.z) out.zIndex = kf.z;
            return out;
        }

        /* ---- initial state ------------------------------------------- */

        this.introMedia.forEach(function (el, i) {
            var kf = KEYFRAMES[i];
            if (!kf) return;
            var s0 = stateFor(kf, 0);
            s0.xPercent = 0;
            s0.yPercent = 0;
            s0.borderRadius = 3 * u;
            gsap.set(el, s0);
        });
        if (this.introInner.length) gsap.set(this.introInner, { clipPath: CLIP.offBottom });

        if (hasSlides) {
            /* y:0 is load-bearing. The CSS start state is translateY(100%),
               which GSAP reads back off the computed matrix as y:<panel>px with
               yPercent:0 — so setting yPercent alone stacks the two and parks
               the carousel a whole panel too low to ever rise into view. */
            gsap.set(this.carousel, { yPercent: 100, y: 0 });
            this.fgItems.forEach(function (el, i) {
                gsap.set(el, { clipPath: clipFor(i) });
            });
            if (this.bgFirstMedia) gsap.set(this.bgFirstMedia, { xPercent: -50, x: 0 });
            if (this.fgItems[0] && this.fgItems[0].firstElementChild) {
                gsap.set(this.fgItems[0].firstElementChild, { clipPath: CLIP.offTop });
            }
            this.bgImages.forEach(function (el, i) {
                el.classList.toggle('is-visible', i === 0);
                el.classList.remove('is-past');
            });
            this.textTl = this.buildTextTimeline();
        }

        /* ---- intro timeline ------------------------------------------ */

        var intro = gsap.timeline({ defaults: { ease: 'none' } });

        /* Thumbnails clip in from the bottom as the pin engages. Timeline units
           map ~1:1 onto scrolled pixels, so this offset is how far you scroll
           past the pin before the first image shows. It was hardcoded at 300;
           it is a section setting now purely so it can be dialled by eye.
           The rest follow 100 units later -- that gap is the lead the first
           image has over the pack, and it rides along with the setting. */
        var revealDelay = parseFloat(this.root.dataset.revealDelay);
        if (!isFinite(revealDelay)) revealDelay = 300;

        if (this.introInner[0]) {
            intro.to(this.introInner[0], { clipPath: CLIP.on, duration: 200 }, revealDelay);
        }
        if (this.introInner.length > 1) {
            intro.to(
                this.introInner.slice(1),
                { clipPath: CLIP.on, duration: 250, stagger: 70, ease: 'power1.inOut' },
                revealDelay + 100
            );
        }

        /* The heading is centred by CSS and deliberately never animated — it
           holds dead centre for the whole intro while the thumbnails move
           around it. */

        /* thumbnails travel to their second position, then off the top.
           lastExitStart tracks when the final thumbnail BEGINS leaving, which is
           where the panel can let go when there are no slides to hand over to. */
        var lastExitStart = 0;
        this.introMedia.forEach(function (el, i) {
            var kf = KEYFRAMES[i];
            if (!kf) return;
            var s1 = stateFor(kf, 1);
            s1.ease = isMobile ? 'power1.in' : S1_EASE_DESKTOP[i];
            s1.duration = sel(S1_DUR_DESKTOP[i], S1_DUR_MOBILE[i]);
            intro.to(el, s1, S1_POS[i]);
            if (s1.y === parkY) lastExitStart = Math.max(lastExitStart, S1_POS[i]);

            if (!isMobile && S2_DUR[i]) {
                var s2 = stateFor(kf, 2);
                s2.ease = 'power1.in';
                s2.duration = S2_DUR[i];
                intro.to(el, s2, S2_POS[i]);
                if (s2.y === parkY) lastExitStart = Math.max(lastExitStart, S2_POS[i]);
            }
        });

        /* first background shrinks to a rounded card, rises, then goes full bleed */
        if (hasSlides && this.bgFirstMedia) {
            intro.to(
                this.bgFirstMedia,
                {
                    width: sel('70%', '90%'),
                    height: sel('70%', '90%'),
                    borderRadius: 5 * u,
                    x: 0,
                    y: sel('-10vh', '-2vh'),
                    ease: 'power1.out',
                    duration: 700
                },
                sel(1150, 500)
            );
            intro.to(
                this.bgFirstMedia,
                {
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    ease: 'power1.in',
                    duration: sel(800, 500)
                },
                sel(2400, 1600)
            );
            intro.to(
                this.bgFirstMedia,
                { y: 0, ease: 'power1.in', duration: sel(1300, 900) },
                sel(1900, 1200)
            );
        }

        /* the whole carousel slides up over the intro */
        if (hasSlides) {
            intro.to(
                this.carousel,
                { yPercent: 0, ease: 'power1.in', duration: sel(1300, 900) },
                sel(1900, 1200)
            );

            if (this.fgItems[0] && this.fgItems[0].firstElementChild) {
                intro.to(
                    this.fgItems[0].firstElementChild,
                    { clipPath: CLIP.on, ease: 'power1.inOut', duration: sel(600, 300) },
                    sel(2850, 1900)
                );
            }
        }

        if (isMobile) intro.timeScale(1.25);

        var introLength = intro.duration() / intro.timeScale();
        var perSlide = hasSlides ? sel(vh * 0.85, vh * 0.7) : 0;
        var slidesLength = perSlide * this.slideCount;
        var total = introLength + slidesLength;

        var master = gsap.timeline({ paused: true });
        master.add(intro);
        if (slidesLength) master.to({}, { duration: slidesLength });
        this.master = master;

        /* How much scroll the panel stays stuck for. With slides that is the
           whole thing — the carousel is the handoff. Without slides, holding on
           until the last thumbnail has fully cleared leaves the panel sitting
           empty, so it releases as the final exit BEGINS and the rest of that
           motion plays out while the section scrolls away. Floor it at 60% of
           the intro so a breakpoint whose exits start early (mobile, where every
           thumbnail leaves in one pass) still gets a proper pinned run. */
        var stickyTrack = total;
        if (!hasSlides && lastExitStart) {
            stickyTrack = Math.max(
                lastExitStart / intro.timeScale(),
                introLength * 0.6
            );
        }

        /* ---- pin ------------------------------------------------------ */

        /* The panel is held in place by CSS `position: sticky`, NOT by
           ScrollTrigger's pin. Sticky needs no pin-spacer, no measured start
           position and no DOM rewrapping, so there is nothing to mis-measure
           and nothing to jump when it lets go: the section is an ordinary tall
           block, the panel sticks under the header while it passes, and the
           moment the section's bottom edge arrives the panel simply resumes
           flowing with the page and the next section follows it up.

           The section's height is what buys the scroll, and it is what decides
           when the panel lets go — so it uses stickyTrack, while the timeline
           below still scrubs over the full `total`. When those differ the tail
           of the animation runs as the section scrolls out, which is exactly
           the overlap we want. */
        this.root.style.height = vh + stickyTrack + 'px';

        this.trigger = ScrollTrigger.create({
            trigger: this.root,
            /* functions so they re-resolve on refresh — the header can change
               height (announcement bar, mobile menu rows). These mirror the
               sticky geometry exactly: the animation starts the frame the panel
               sticks and ends the frame it releases. */
            start: function () {
                var h = headerOffset();
                self.root.style.setProperty('--rf-header-h', h + 'px');
                return 'top top+=' + h;
            },
            /* explicit length rather than 'bottom bottom': the timeline can now
               outlast the sticky track, so it must not be tied to the section's
               bottom edge. Identical to 'bottom bottom' whenever they match. */
            end: '+=' + total,
            animation: master,
            scrub: isTouch ? true : 1,
            invalidateOnRefresh: true,
            onUpdate: function (st) {
                if (!hasSlides) return;
                var px = st.progress * total;
                var raw = (px - introLength) / perSlide;
                self.setSlide(raw < 0 ? -1 : Math.min(self.slideCount - 1, Math.floor(raw)));
            },
            snap: this.snap && hasSlides
                ? {
                      snapTo: function (value) {
                          var px = value * total;
                          if (px < introLength) return value;
                          var raw = (px - introLength) / perSlide;
                          /* land on the middle of a slide band; leave the tail
                             after the last band free so the pin can be exited */
                          if (raw > self.slideCount - 0.5) return value;
                          var target = Math.max(0.5, Math.round(raw - 0.5) + 0.5);
                          return (introLength + target * perSlide) / total;
                      },
                      duration: { min: 0.2, max: 0.45 },
                      delay: 0.3,
                      inertia: false,
                      directional: false,
                      ease: 'power1.inOut'
                  }
                : undefined
        });
    };

    Spotlight.prototype.buildTextTimeline = function () {
        var gsap = window.gsap;
        var inners = this.textInners;
        if (!inners.length) return null;

        gsap.set(inners, { yPercent: 100, rotateX: -100, transformOrigin: 'top', opacity: 0 });

        var tl = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
        tl.addLabel('is-0');
        inners.forEach(function (el, i) {
            var step = gsap.timeline({ defaults: { ease: 'power1.inOut', duration: 1 } });
            if (i > 0) {
                step.set(inners[i - 1], { transformOrigin: 'bottom' });
                step.to(inners[i - 1], { yPercent: -100, rotateX: 100 });
                step.to(inners[i - 1], { opacity: 0, ease: 'power1.out', duration: 0.3 }, '-=0.8');
            }
            step.set(el, { transformOrigin: 'top' });
            step.to(el, { yPercent: 0, rotateX: 0 }, i > 0 ? '-=1' : 0);
            step.to(el, { opacity: 1, ease: 'power1.in', duration: 0.3 }, '-=0.6');
            tl.add(step);
            tl.addLabel('is-' + (i + 1));
        });
        return tl;
    };

    Spotlight.prototype.setSlide = function (index) {
        var gsap = window.gsap;
        var visible = Math.max(index, 0);

        if (visible !== this.index) {
            this.index = visible;
            this.bgImages.forEach(function (el, i) {
                el.classList.toggle('is-visible', visible >= i);
                el.classList.toggle('is-past', visible > i);
            });
            this.fgItems.forEach(function (el, i) {
                gsap.to(el, {
                    clipPath: clipFor(i - visible),
                    duration: 1,
                    ease: THE_CURVE,
                    overwrite: 'auto'
                });
            });
        }

        var label = index < 0 ? 'is-0' : 'is-' + (index + 1);
        if (label !== this.label && this.textTl && this.textTl.labels[label] !== undefined) {
            this.label = label;
            gsap.to(this.textTl, {
                progress: this.textTl.labels[label] / this.textTl.duration(),
                ease: 'power1.inOut',
                duration: 1,
                overwrite: true
            });
        }
    };

    /* ------------------------------------------------------------ helpers */

    function clipFor(delta) {
        if (delta === 0) return CLIP.on;
        return delta > 0 ? CLIP.offTop : CLIP.offBottom;
    }

    function toArray(nodeList) {
        return Array.prototype.slice.call(nodeList);
    }

    function debounce(fn, wait) {
        var t;
        return function () {
            var args = arguments;
            var ctx = this;
            clearTimeout(t);
            t = setTimeout(function () {
                fn.apply(ctx, args);
            }, wait);
        };
    }

    /* --------------------------------------------------------------- boot */

    function init(root) {
        /* Claim the element synchronously. loadLibs() is async, so checking
           __rfSpotlight (which is only set after it resolves) lets a second
           initAll() — a re-run of this script, or a shopify:section:load that
           lands mid-load — build a SECOND pin on the same element. Two pins
           means two pin-spacers, double the scroll length, and the panel
           reappearing further down the page as if it were another section. */
        if (!root || root.__rfSpotlightClaimed) return;
        root.__rfSpotlightClaimed = true;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            root.classList.add('is-static');
            return;
        }
        loadLibs()
            .then(function () {
                if (root.__rfSpotlight || !document.body.contains(root)) return;
                var instance = new Spotlight(root);
                root.__rfSpotlight = instance;
                instance.mount();
            })
            .catch(function (err) {
                root.classList.add('is-static');
                if (window.console) console.error('[runway-spotlight]', err);
            });
    }

    function initAll(scope) {
        toArray((scope || document).querySelectorAll('[data-rf-spotlight]')).forEach(init);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            initAll();
        });
    } else {
        initAll();
    }

    document.addEventListener('shopify:section:load', function (e) {
        initAll(e.target);
    });

    document.addEventListener('shopify:section:unload', function (e) {
        var root = e.target.querySelector('[data-rf-spotlight]');
        if (!root) return;
        if (root.__rfSpotlight) root.__rfSpotlight.destroy();
        root.__rfSpotlight = null;
        root.__rfSpotlightClaimed = false;
    });
})();
